type AttributeValue = string | number | boolean | object | null;

interface Attribute {
  key: string;
  value: AttributeValue;
}

const ATTRIBUTE_LABELS: Record<string, string> = {
  pets: 'Pets',
  veiculo: 'Veículos',
  type_flag: 'Perfil',
  document: 'CPF',
  status_flag: 'Status',
  nationality: 'Nacionalidade',
  birth_date: 'Data de Nascimento',
  gender_flag: 'Gênero',
  marital_flag: 'Estado Civil',
  phone_number: 'Telefone',
  cnh: 'CNH',
  unit: 'Unidade',
  section_name: 'Seção',
};

const GENDER_TRANSLATIONS: Record<string, string> = {
  MALE: 'Masculino',
  FEMALE: 'Feminino',
  OTHER: 'Outro',
};
const MARITAL_TRANSLATIONS: Record<string, string> = {
  SINGLE: 'Solteiro(a)',
  MARRIED: 'Casado(a)',
  DIVORCED: 'Divorciado(a)',
  WIDOWED: 'Viúvo(a)',
};

const STATUS_TRANSLATIONS: Record<string, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  APPROVED: 'Aprovado',
  PENDING: 'Pendente',
  BLOCKED: 'Bloqueado',
  SUSPENDED: 'Suspenso',
  AVAILABLE: 'Disponível',
  UNAVAILABLE: 'Indisponível',
  ENABLED: 'Habilitado',
  DISABLED: 'Desabilitado',
};

const TYPE_TRANSLATIONS: Record<string, string> = {
  ADMIN: 'Administrador',
  USER: 'Usuário',
  EMPLOYEE: 'Funcionário',
  MANAGER: 'Gerente',
  VISITOR: 'Visitante',
  RESIDENT: 'Morador',
  OWNER: 'Proprietário',
  TENANT: 'Inquilino',
};

function formatValue(key: string, value: AttributeValue): string | JSX.Element {

  if (key === 'gender_flag') {
    const val = Array.isArray(value) && value[0] && typeof value[0] === 'object' && 'value' in value[0]
      ? value[0].value
      : value;
    return GENDER_TRANSLATIONS[val as string] || val || '-';
  }
 
  if (key === 'marital_flag') {
    const val = Array.isArray(value) && value[0] && typeof value[0] === 'object' && 'value' in value[0]
      ? value[0].value
      : value;
    return MARITAL_TRANSLATIONS[val as string] || val || '-';
  }

  if (key === 'status_flag') {
    const val = Array.isArray(value) && value[0] && typeof value[0] === 'object' && 'value' in value[0]
      ? value[0].value
      : value;
    return STATUS_TRANSLATIONS[val as string] || val || '-';
  }

  if (key === 'type_flag') {
    const val = Array.isArray(value) && value[0] && typeof value[0] === 'object' && 'value' in value[0]
      ? value[0].value
      : value;
    return TYPE_TRANSLATIONS[val as string] || val || '-';
  }


  const safeStringify = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
      return String(val);
    }
    if (typeof val === 'object') {
      return JSON.stringify(val);
    }
    return String(val);
  };

  if (Array.isArray(value) && value.length > 0 && value[0]?.value !== undefined) {
    if (value.length === 1) {
      return safeStringify(value[0].value);
    }
    return (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {value.map((v: any, idx: number) => (
          <li key={v.id || idx}>{safeStringify(v.value)}</li>
        ))}
      </ul>
    );
  }

  if (Array.isArray(value) && typeof value[0] === 'string') {
    return value[0];
  }

  if (key === 'pets' && Array.isArray(value)) {
    return value.length === 0
      ? '-'
      : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {value.map((pet: any, idx: number) => (
            <li key={pet.id || idx}>
              {safeStringify(pet.name)} ({safeStringify(pet.specie)}){pet.status ? ` - ${STATUS_TRANSLATIONS[pet.status] || pet.status}` : ''}
            </li>
          ))}
        </ul>
      );
  }

  if (key === 'veiculo' && Array.isArray(value)) {
    return value.length === 0
      ? '-'
      : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {value.map((v: any, idx: number) => (
            <li key={v.placa || idx}>
              {safeStringify(v.marca)} {safeStringify(v.modelo)} ({safeStringify(v.placa)}) - {safeStringify(v.ano)} - {safeStringify(v.cor)}
            </li>
          ))}
        </ul>
      );
  }

  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  
  return safeStringify(value);
}
export function getAttributeLabelAndValue(keys: string[], attributes: Attribute[]): { title: string; value: string | JSX.Element } {
  for (const key of keys) {
    const attr = attributes.find(a => a.key === key);
    if (attr) {
      console.log('getAttributeLabelAndValue', key, attr);
      return {
        title: ATTRIBUTE_LABELS[key] || key,
        value: formatValue(key, attr.value),
      };
    }
  }
  return { title: '', value: '' };
}


export function getUniqueAttributeLabelsAndValues(attributes: Attribute[]): { title: string; value: string | JSX.Element; key: string }[] {
  const seen = new Set<string>();
  const result: { title: string; value: string | JSX.Element; key: string }[] = [];
  for (const attr of attributes) {
    if (!seen.has(attr.key)) {
      seen.add(attr.key);
      result.push({
        key: attr.key,
        title: ATTRIBUTE_LABELS[attr.key] || attr.key,
        value: formatValue(attr.key, attr.value),
      });
    }
  }
  return result;
}
export function getAttrValue(attributes: Attribute[] = [], key: string) {
  const attr = attributes.find(a => a.key === key);
  if (!attr) return '';

  if (Array.isArray(attr.value) && attr.value.length > 0 && typeof attr.value[0] === 'object' && 'value' in attr.value[0]) {
    return attr.value[0].value;
  }
  return '';
}


export function getCnhNumber(attributes: Attribute[]) {
  const raw = getAttrValue(attributes, 'cnh');
  if (typeof raw === 'string' && raw.includes(' - ')) {
    return raw.split(' - ')[0];
  }
  return '';
}
export function getCnhExpiration(attributes: Attribute[]) {
  const raw = getAttrValue(attributes, 'cnh');
  if (typeof raw === 'string' && raw.includes(' - ')) {
    return raw.split(' - ')[1];
  }
  return '';
}


// Função auxiliar para extrair valores dos atributos (que vêm como arrays)
export const getAttributeValue = (attributes: any[], key: string): any => {
  if (!attributes || !Array.isArray(attributes)) {
    console.log(`Attributes não é um array para key: ${key}`, attributes);
    return null;
  }
  
  const attribute = attributes.find(a => a.key === key);
  if (!attribute || !attribute.value) {
    console.log(`Attribute não encontrado para key: ${key}`);
    return null;
  }

  // Formato novo: array de objetos com id e value
  if (Array.isArray(attribute.value)) {
    if (attribute.value.length > 0) {
      // Se tem objetos com propriedade 'value'
      if (typeof attribute.value[0] === 'object' && attribute.value[0].value !== undefined) {
        return attribute.value[0].value;
      }
      // Se é array simples (formato antigo)
      return attribute.value[0];
    }

    return null;
  }

  // Formato direto (se não for array)
  return attribute.value;
};

// Função para extrair número da CNH dos attributes - suporta ambos os formatos
export const getCnhNumberFromAttributes = (attributes: any[]): string => {
  if (!attributes || !Array.isArray(attributes)) return '';
  
  const cnhAttr = attributes.find(a => a.key === 'cnh');
  if (!cnhAttr || !cnhAttr.value) return '';
  
  let cnhValue = '';
  
  // Formato novo: array de objetos com id e value
  if (Array.isArray(cnhAttr.value) && cnhAttr.value.length > 0) {
    if (typeof cnhAttr.value[0] === 'object' && cnhAttr.value[0].value !== undefined) {
      cnhValue = cnhAttr.value[0].value;
    } else {
      cnhValue = cnhAttr.value[0];
    }
  } else {
    cnhValue = cnhAttr.value;
  }
  
  // Se o valor é uma string com formato "number - date"
  if (typeof cnhValue === 'string' && cnhValue.includes(' - ')) {
    return cnhValue.split(' - ')[0] || '';
  }
  
  // Se o valor é um objeto com number
  if (typeof cnhValue === 'object' && cnhValue?.number) {
    return cnhValue.number;
  }
  
  return cnhValue || '';
};

// Função para extrair data de expiração da CNH - suporta ambos os formatos
export const getCnhExpirationFromAttributes = (attributes: any[]): string => {
  if (!attributes || !Array.isArray(attributes)) return '';
  
  const cnhAttr = attributes.find(a => a.key === 'cnh');
  if (!cnhAttr || !cnhAttr.value) return '';
  
  let cnhValue = '';
  
  // Formato novo: array de objetos com id e value
  if (Array.isArray(cnhAttr.value) && cnhAttr.value.length > 0) {
    if (typeof cnhAttr.value[0] === 'object' && cnhAttr.value[0].value !== undefined) {
      cnhValue = cnhAttr.value[0].value;
    } else {
      cnhValue = cnhAttr.value[0];
    }
  } else {
    cnhValue = cnhAttr.value;
  }
  
  // Se o valor é uma string com formato "number - date"
  if (typeof cnhValue === 'string' && cnhValue.includes(' - ')) {
    return cnhValue.split(' - ')[1] || '';
  }
  
  // Se o valor é um objeto com expiration_date
  if (typeof cnhValue === 'object' && cnhValue?.expiration_date) {
    return cnhValue.expiration_date;
  }
  
  return '';
};

